/* eslint-disable max-lines-per-function */
import { findIndexById } from "common/utils/helpers/find-index-by-id";
import { submitDtfEvent } from "common/utils/mixpanel/submit-dtf-events";
import UseSvg from "common/vue/components/UseSvg.react.vue";
import CheckBox from "components/jiffy/react-check-box/CheckBox.vue";
import { api } from "jiffy-api";
import debounce from "lodash.debounce";
import React, { useEffect, useRef, useState } from "react";
import { cn } from "tailwind-variants";
import RemoveBackground from "./ArtworkQualityConfigPanel/components/RemoveBackground.vue";
import { useSpanglesStore } from "./spangles-module";
import { useStickersStore } from "./stickers-module";
import { useDtfStore } from "./store";
import { hasGangSheetError } from "./utils";
import "./TrimInfo.scss";

const REMOVE_BACKGROUND_SWITCH_DEBOUNCE = 3000;

export default function TrimInfo({
  toggleBackgroundError = null,
  removeBackground = true,
  defaultRemoveBackground = true,
  canRemoveBg = false,
  id,
  paddingSize = "no_padding",
  artwork = null,
  activeConfigSection = null,
  ...restProps
}) {
  const [backgroundRemovedSwitch, setBackgroundRemovedSwitch] = useState(true);
  const [userForceBgRemove, setUserForceBgRemove] = useState(false);
  const [annotateMode, setAnnotateMode] = useState("keep");

  const dtfStore = useDtfStore();
  const stickersStore = useStickersStore();
  const spanglesStore = useSpanglesStore();

  function sticker() {
    return dtfStore.sticker;
  }

  function allowForceBgRemove() {
    return dtfStore.allowForceBgRemove;
  }

  function productId() {
    return dtfStore.productId;
  }

  function experiments() {
    return dtfStore.experiments;
  }

  function isProColor() {
    return dtfStore.isProColor;
  }

  function isSpangle() {
    return dtfStore.isSpangle;
  }

  function isStandardArtwork() {
    return dtfStore.isStandardArtwork;
  }

  function enableAbAiMonetization() {
    return dtfStore.enableAbAiMonetization;
  }

  function artworks() {
    return dtfStore.artworks;
  }

  function businessLine() {
    return dtfStore.businessLine;
  }

  function enableBackgroundRemovalToggle() {
    return isStandardArtwork() ? artwork?.enhancementCompleted : true;
  }

  function hasGangSheetErrorLocal() {
    return hasGangSheetError(artwork);
  }

  function annotateEnabled() {
    return artwork?.annotateEnabled;
  }

  function undoEnabled() {
    const { revisionImageUrls = [], revisionPosition = 0 } = artwork || {};
    return revisionPosition > 0 && revisionImageUrls[revisionPosition - 1] !== undefined;
  }

  function redoEnabled() {
    const { revisionImageUrls = [], revisionPosition = 0 } = artwork || {};
    return (
      revisionPosition < revisionImageUrls.length - 1 &&
      revisionImageUrls[revisionPosition + 1] !== undefined
    );
  }

  function updateArtworkStyle(...args) {
    return dtfStore.updateArtworkStyle(...args);
  }

  function sendUpdateArtworkStyle(...args) {
    return dtfStore.sendUpdateArtworkStyle(...args);
  }

  function loadArtworkAndSetPaddingImage(...args) {
    return dtfStore.loadArtworkAndSetPaddingImage(...args);
  }

  function generateCmykImages(...args) {
    return dtfStore.generateCmykImages(...args);
  }

  function generateSpangleImage(...args) {
    return dtfStore.generateSpangleImage(...args);
  }

  function updateArtworkValuesById(...args) {
    return dtfStore.updateArtworkValuesById(...args);
  }

  function setPendingPinningInvalidation(...args) {
    return dtfStore.setPendingPinningInvalidation(...args);
  }

  function preCut() {
    return dtfStore.preCut;
  }

  function generateStickerImage(...args) {
    return stickersStore.generateStickerImage(...args);
  }

  function resetSpangles(...args) {
    return spanglesStore.resetSpangles(...args);
  }

  const updateToggleBackgroundRef = useRef(null);

  useEffect(() => {
    updateToggleBackgroundRef.current = debounce(removed => {
      const paddingSizeValue = artwork?.paddingSize || "no_padding";
      sendUpdateArtworkStyle({
        id,
        isBgRemoved: removed,
        padding_size: paddingSizeValue,
        forceBgRemoval: userForceBgRemove
      });
    }, REMOVE_BACKGROUND_SWITCH_DEBOUNCE);
  }, [id, artwork, userForceBgRemove, sendUpdateArtworkStyle]);

  useEffect(() => {
    handleAnnotateModeChange();
  }, [annotateMode]);

  useEffect(() => {
    if (activeConfigSection === "jiffy-print-perfect" && !removeBackground) {
      handleRemoveBackgroundChange(true);
    }
  }, [activeConfigSection, removeBackground]);

  useEffect(() => {
    if (enableAbAiMonetization()) {
      if (activeConfigSection === "jiffy-print-perfect" && !removeBackground) {
        handleRemoveBackgroundChange(true);
      }
    }
  }, []);

  function handleRemoveBackgroundChange(arg) {
    const isManuallyToggled = typeof arg !== "boolean";
    const newValue = isManuallyToggled ? !removeBackground : arg;
    if (artwork?.pinningSessionId) {
      if (arg?.target) {
        arg.target.checked = removeBackground;
      }
      setPendingPinningInvalidation({
        type: "removeBackground",
        artworkId: artwork.id,
        value: newValue
      });
      return;
    }
    setTimeout(() => {
      executeRemoveBackgroundChange(newValue, isManuallyToggled);
    }, 0);
  }

  function executeRemoveBackgroundChange(isBgRemoved, isManuallyToggled = false) {
    setBackgroundRemovedSwitch(isBgRemoved);
    const artworkIndex = findIndexById(artworks(), id);
    const artworkAtIndex = artworks()[artworkIndex];
    if (isStandardArtwork() && !artworkAtIndex?.enhancementCompleted) {
      return;
    }
    submitDtfEvent("remove_background_toggled", {
      artwork_id: artwork.id,
      product_id: productId(),
      previous_state: removeBackground,
      new_state: isBgRemoved,
      active_config_section: activeConfigSection,
      business_line: businessLine(),
      is_manually_toggled: isManuallyToggled
    });
    resetSpangles();
    updateArtworkStyle({
      id,
      isBgRemoved
    });
    if (sticker()) {
      generateStickerImage({
        artwork,
        removeBackground: isBgRemoved
      });
    }
    updateToggleBackgroundRef.current?.(isBgRemoved);
  }

  function handleManualBgRemoval(event) {
    setUserForceBgRemove(event.target.checked);
    handleRemoveBackgroundChange(event);
    submitDtfEvent("manual_bg_removal_toggle", {
      artwork
    });
  }

  function handleAnnotateClick(value) {
    updateArtworkValuesById({
      id,
      annotateEnabled: value
    });
  }

  function handleAnnotateModeChange() {
    updateArtworkValuesById({
      id,
      annotateMode
    });
  }

  async function segmentImage() {
    if (!artwork?.annotatedPoints || artwork.annotatedPoints.length === 0) return;
    const response = await api.dtf.segmentImage({
      id,
      points: artwork.annotatedPoints,
      canvas_width: artwork.annotatedCanvasWidth,
      canvas_height: artwork.annotatedCanvasHeight,
      product_id: productId()
    });
    if (isProColor()) {
      await generateCmykImages({
        artwork
      });
    }
    if (isSpangle()) {
      await generateSpangleImage({
        artwork
      });
    }
    const updatedAttributes = {
      url: response.image_url,
      metadata: response.metadata,
      revisionImageUrls: response.revision_image_urls,
      revisionPosition: response.revision_image_urls.length - 1,
      annotatedPoints: [],
      originalImageUrl: response.original_image_url,
      originalNoBgUrl: response.original_no_bg_url,
      stickerCutlineNoBgUrl: response.sticker_cutline_no_bg_url,
      stickerCutlineWithBgUrl: response.sticker_cutline_with_bg_url
    };
    updateArtworkValuesById({
      id,
      ...updatedAttributes
    });
    if (sticker()) {
      const updatedArtwork = {
        ...artwork,
        ...updatedAttributes
      };
      await generateStickerImage({
        artwork: updatedArtwork,
        useCache: false
      });
    }
  }

  async function handleRevisionToggle(revisionType) {
    const { revisionPosition = 0 } = artwork || {};
    const newPosition = revisionType === "undo" ? revisionPosition - 1 : revisionPosition + 1;
    const response = await api.dtf.toggleSegmentVersion({
      id,
      position: newPosition,
      toggle_type: revisionType,
      product_id: productId()
    });
    if (isProColor()) {
      await generateCmykImages({
        artwork
      });
    }
    if (isSpangle()) {
      await generateSpangleImage({
        artwork
      });
    }
    const updatedAttributes = {
      url: response.image_url,
      metadata: response.metadata,
      revisionPosition: newPosition,
      originalImageUrl: response.original_image_url,
      originalNoBgUrl: response.original_no_bg_url,
      stickerCutlineNoBgUrl: response.sticker_cutline_no_bg_url,
      stickerCutlineWithBgUrl: response.sticker_cutline_with_bg_url
    };
    updateArtworkValuesById({
      id,
      ...updatedAttributes
    });
    if (sticker()) {
      const updatedArtwork = {
        ...artwork,
        ...updatedAttributes
      };
      await generateStickerImage({
        artwork: updatedArtwork,
        useCache: false
      });
    }
  }

  return (
    <div {...restProps} className={cn("trim-info scope-d9968961", restProps.className)}>
      {isStandardArtwork() ? (
        <>
          {enableAbAiMonetization() && canRemoveBg && !hasGangSheetErrorLocal() ? (
            <RemoveBackground
              className={cn("scope-d9968961", {
                "trim-info__remove-toggle--disabled": !enableBackgroundRemovalToggle()
              })}
              value={removeBackground}
              disabled={!enableBackgroundRemovalToggle()}
              onChange={handleRemoveBackgroundChange}
              data-testid="remove-background"
            />
          ) : canRemoveBg && !hasGangSheetErrorLocal() ? (
            <button
              className={cn("trim-info__remove-toggle scope-d9968961", {
                "trim-info__remove-toggle--selected": removeBackground,
                "trim-info__remove-toggle--disabled": !enableBackgroundRemovalToggle()
              })}
              type="button"
              onClick={handleRemoveBackgroundChange}
              data-testid="trim-info-remove-toggle"
            >
              <UseSvg
                className="trim-info__remove-toggle-icon scope-d9968961"
                icon="check"
                data-testid="trim-info-remove-toggle-icon"
              />

              <span className="trim-info__remove-toggle-label scope-d9968961">
                Remove Background
              </span>
            </button>
          ) : null}
        </>
      ) : !isStandardArtwork() ? (
        <>
          {canRemoveBg && !hasGangSheetErrorLocal() ? (
            <CheckBox
              className="scope-d9968961"
              label="Remove Background"
              style={"gray"}
              disable={!enableBackgroundRemovalToggle()}
              data-testid="check-box"
            >
              <input
                className="scope-d9968961"
                type="checkbox"
                checked={removeBackground}
                disabled={!enableBackgroundRemovalToggle()}
                onChange={handleRemoveBackgroundChange}
                data-testid="trim-info-input"
              />
            </CheckBox>
          ) : null}
        </>
      ) : allowForceBgRemove() ? (
        <CheckBox
          className="scope-d9968961"
          label="Remove Background"
          style={"gray"}
          disabled={!enableBackgroundRemovalToggle()}
          data-testid="check-box-1"
        >
          <input
            className="scope-d9968961"
            type="checkbox"
            checked={userForceBgRemove}
            disabled={!enableBackgroundRemovalToggle()}
            onChange={handleManualBgRemoval}
            data-testid="trim-info-input-1"
          />
        </CheckBox>
      ) : null}
      {toggleBackgroundError ? (
        <div className="trim-info__wrapper trim-info__wrapper--error scope-d9968961">
          <UseSvg
            className="trim-info__icon scope-d9968961"
            icon="warning"
            data-testid="trim-info-icon"
          />

          <span className="scope-d9968961">{toggleBackgroundError}</span>
        </div>
      ) : null}
    </div>
  );
}
